import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";

import Map, { Source, Layer, NavigationControl } from "react-map-gl/mapbox";
import Box from "@mui/material/Box";

import SideMenu from "./sideMenu";
import MunicipalityMapMenu from "../shared/municipalityMapMenu";
import {
  MAP_STYLE,
  FIRE_HAZARD_COLOR_STOPS,
  EARTHQUAKE_HAZARD_COLOR_STOPS,
  FLOOD_HAZARD_COLOR_STOPS_Z13,
  floodHazardLayer,
  FLOOD_HAZARD_TILESETS,
  buildFloodHazardFillColor,
  floodRiversLayer,
  floodLakesLayer,
  MAPBOX_TILES,
  EARTHQUAKE_HAZARD_SOURCE_URL,
  EARTHQUAKE_TECTONIC_SOURCE_URL,
  EARTHQUAKE_FAULTS_SOURCE_URL,
  earthquakeHazardFillLayer,
  earthquakeTectonicPlatesLayer,
  earthquakeActiveFaultsLayer,
  FIRE_TILES_HIGH_ZOOM,
  fireGenericLayer,
  FIRE_TILES_LOW_ZOOM,
} from "./constants";
import Marker from "./marker";
import CoordinatesPopup from "./coordinatesPopup";
import MapSearch from "../shared/mapSearch";
import useSidebarWidth from "../../shared/hooks/useSidebarWidth";
import { MapLayerOrderEnforcer } from "../shared/mapLayerOrder";
import { useLayerOpacities } from "../shared/useLayerOpacities";
import { useShow3dObjects } from "../shared/useShow3dObjects";
import { getNearbyWaterFeature, getWaterFeatureName } from "./helpers";
import CircleSource from "./circleSource";
import WaterFeaturePopup from "./waterFeaturePopup";
import FloodDistanceMarkers from "./floodDistanceMarkers/floodDistanceMarkers";
import AssessmentHistoryMarkers from "./markers/AssessmentHistoryMarkers/assessmentHistoryMarkers";
import { useClient } from "../../contexts/ClientContext";
import { getMunicipalityMapScope } from "../shared/mapInitialView";
import MunicipalityScopeLayers, {
  MUNICIPALITY_SCOPE_LAYER_IDS,
} from "../shared/municipalityScopeLayers";

import "mapbox-gl/dist/mapbox-gl.css";

const FALLBACK_INITIAL_VIEW_STATE = {
  longitude: 23.7348324,
  latitude: 37.9755648,
  zoom: 10,
};

function MunicipalityRisksMap({
  mapRef,
  markerInfo,
  setMarkerInfo,
  setFormValues,
  activeSideMenuTab,
  setActiveSideMenuTab,
  historyAssessmentsForMap,
  selectedEarthquakeLayers,
  setSelectedEarthquakeLayers,
  selectedFloodLayers,
  setSelectedFloodLayers,
  initialViewState,
  mapInstanceKey,
  allowedMunicipalityIds,
}) {
  const [activeRisk, setActiveRisk] = useState("fire");
  const [fireMapMode, setFireMapMode] = useState("hazard");
  const [activeMapStyle, setActiveMapStyle] = useState(MAP_STYLE.street);
  const [rightClickCoords, setRightClickCoords] = useState(null);
  // 3D terrain starts OFF and is toggled from the map menu. 3D buildings come
  // from the Mapbox Standard style and are shown by default, but can be toggled.
  const [show3DMap, setShow3DMap] = useState(false);
  const [show3DBuildings, setShow3DBuildings] = useState(true);
  const [hoveredWaterFeature, setHoveredWaterFeature] = useState(null);
  const { layerOpacities, setLayerOpacity } = useLayerOpacities();

  // Satellite (Standard Satellite) has no 3D buildings, so force them off there
  // while keeping the user's toggle preference for when they return to street.
  useShow3dObjects(
    mapRef,
    show3DBuildings && activeMapStyle !== MAP_STYLE.satellite,
  );

  const onMapClick = useCallback((e) => {
    if (e?.originalEvent?.target?.closest?.('[data-history-marker="true"]')) {
      return;
    }

    // Clicks on the assessment marker are handled by the DOM marker itself
    // (it stops propagation), so any click reaching the map sets coordinates.
    const { lng, lat } = e.lngLat;
    setRightClickCoords({ lat, lng });
  }, []);

  const onLocationSelect = useCallback(
    ({ lat, lng }) => {
      mapRef.current?.getMap()?.flyTo({ center: [lng, lat] });
      setRightClickCoords({ lat, lng });
    },
    [mapRef],
  );

  const onMapMouseMove = useCallback(
    (event) => {
      if (activeRisk !== "flood") {
        if (hoveredWaterFeature) {
          setHoveredWaterFeature(null);
        }
        return;
      }

      const map = mapRef.current?.getMap();
      if (!map) {
        return;
      }

      const nearbyFeature = getNearbyWaterFeature({
        map,
        point: event.point,
        activeRisk,
        riversLayerId: floodRiversLayer.id,
        lakesLayerId: floodLakesLayer.id,
        disableRivers: true,
      });

      if (!nearbyFeature) {
        if (hoveredWaterFeature) {
          setHoveredWaterFeature(null);
        }
        map.getCanvas().style.cursor = "";
        return;
      }

      map.getCanvas().style.cursor = "pointer";
      setHoveredWaterFeature({
        name: getWaterFeatureName(nearbyFeature),
        longitude: event.lngLat.lng,
        latitude: event.lngLat.lat,
      });
    },
    [activeRisk, hoveredWaterFeature, mapRef],
  );

  const onMapMouseLeave = useCallback(() => {
    if (hoveredWaterFeature) {
      setHoveredWaterFeature(null);
    }
    const map = mapRef.current?.getMap();
    if (map) {
      map.getCanvas().style.cursor = "";
    }
  }, [hoveredWaterFeature, mapRef]);

  const interactiveLayerIds = useMemo(() => {
    const ids = [];
    if (activeRisk === "flood") {
      ids.push(floodRiversLayer.id, floodLakesLayer.id);
    }

    return ids;
  }, [activeRisk]);

  useEffect(() => {
    if (activeRisk === "fire") {
      setFireMapMode("hazard");
    }
  }, [activeRisk]);

  useEffect(() => {
    // Clear transient map popups/overlays when switching side-menu tabs.
    setRightClickCoords(null);
    setHoveredWaterFeature(null);
    setMarkerInfo(null);
  }, [activeSideMenuTab, setMarkerInfo]);

  const isFireHazardModeActive =
    activeRisk === "fire" && fireMapMode === "hazard";

  const fireHazardFillColor = useMemo(() => {
    const [firstStop, ...remainingStops] = FIRE_HAZARD_COLOR_STOPS;
    const defaultColor = firstStop?.[1] ?? "#9efc9c";

    return [
      "step",
      ["coalesce", ["to-number", ["get", "risk_fire"]], 0],
      defaultColor,
      ...remainingStops.flat(),
    ];
  }, []);

  const showEarthquakeHazardLayer =
    activeRisk === "earthquake" && selectedEarthquakeLayers.includes("hazard");
  const showEarthquakeTectonicLayer =
    activeRisk === "earthquake" &&
    selectedEarthquakeLayers.includes("tectonicPlates");
  const showEarthquakeFaultsLayer =
    activeRisk === "earthquake" &&
    selectedEarthquakeLayers.includes("activeFaults");

  const showFloodHazardLayer =
    activeRisk === "flood" && selectedFloodLayers.includes("hazard");
  const showFloodRiversLayer =
    activeRisk === "flood" && selectedFloodLayers.includes("rivers");
  const showFloodLakesLayer =
    activeRisk === "flood" && selectedFloodLayers.includes("lakes");

  const selectedHistoryAssessmentId =
    markerInfo?.assessmentId || markerInfo?.riskData?.id;

  return (
    <div
      style={{
        width: "100%",
        height: "calc(100vh - 64px)",
        position: "relative",
      }}
    >
      <MapSearch
        onLocationSelect={onLocationSelect}
        placeholderKey="risksMap.mapSearchPlaceholder"
      />

      <Map
        key={mapInstanceKey}
        ref={mapRef}
        style={{ width: "100%", height: "100%" }}
        mapStyle={activeMapStyle}
        projection="mercator"
        terrain={
          show3DMap ? { source: "mapbox-dem", exaggeration: 1.2 } : undefined
        }
        initialViewState={initialViewState}
        mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
        onClick={onMapClick}
        onMouseMove={onMapMouseMove}
        onMouseLeave={onMapMouseLeave}
        interactiveLayerIds={interactiveLayerIds}
        maxPitch={85}
      >
        <NavigationControl position="top-right" />

        <MapLayerOrderEnforcer />

        {/* --- 3D TERRAIN DEM SOURCE --- */}
        <Source
          id="mapbox-dem"
          type="raster-dem"
          url="mapbox://mapbox.mapbox-terrain-dem-v1"
          tileSize={512}
          maxzoom={14}
        />

        {rightClickCoords && (
          <CoordinatesPopup
            rightClickCoords={rightClickCoords}
            setRightClickCoords={setRightClickCoords}
            onAdd={(coords) => {
              setActiveSideMenuTab("assessment");
              setFormValues((prev) => ({
                ...prev,
                ...coords,
                place: null,
                activeAddressType: "coordinates",
                showResults: false,
              }));
            }}
          />
        )}

        {activeSideMenuTab === "history" && (
          <AssessmentHistoryMarkers
            assessments={historyAssessmentsForMap}
            mapRef={mapRef}
            selectedAssessmentId={selectedHistoryAssessmentId}
            setMarkerInfo={setMarkerInfo}
          />
        )}

        {markerInfo &&
          (activeSideMenuTab === "assessment" ? (
            <Marker markerInfo={markerInfo} setMarkerInfo={setMarkerInfo} />
          ) : (
            <Marker
              markerInfo={markerInfo}
              setMarkerInfo={setMarkerInfo}
              showPoint={false}
            />
          ))}

        {activeRisk === "flood" && (
          <WaterFeaturePopup feature={hoveredWaterFeature} />
        )}

        {markerInfo && activeRisk === "fire" && (
          <CircleSource
            center={[
              markerInfo.riskData.longitude,
              markerInfo.riskData.latitude,
            ]}
          />
        )}

        {markerInfo && activeRisk === "flood" && (
          <FloodDistanceMarkers markerInfo={markerInfo} />
        )}

        {activeRisk === "fire" && fireMapMode === "terrain" && (
          <>
            {FIRE_TILES_HIGH_ZOOM.map((tileset, index) => {
              return (
                <Source
                  key={index}
                  id={`fire-${index}`}
                  type="vector"
                  url={tileset.url}
                >
                  <Layer
                    {...fireGenericLayer}
                    id={`fire-layer-${index}`}
                    source-layer={tileset["source-layer"]}
                    minzoom={11}
                    paint={{
                      ...fireGenericLayer.paint,
                      "fill-opacity": layerOpacities.fire,
                    }}
                  />
                </Source>
              );
            })}

            {FIRE_TILES_LOW_ZOOM.map((tileset, index) => {
              return (
                <Source
                  key={index}
                  id={`fire-low-${index}`}
                  type="vector"
                  url={tileset.url}
                >
                  <Layer
                    {...fireGenericLayer}
                    id={`fire-low-layer-${index}`}
                    source-layer={tileset["source-layer"]}
                    maxzoom={11}
                    paint={{
                      ...fireGenericLayer.paint,
                      "fill-opacity": layerOpacities.fire,
                    }}
                  />
                </Source>
              );
            })}
          </>
        )}

        {isFireHazardModeActive && (
          <Source
            id="fire-hazard-source"
            url="mapbox://georgemouts.7l4wxdqb"
            type="vector"
          >
            <Layer
              id="fire-hazard-layer-fill"
              type="fill"
              source-layer="skiathos_grid_risk"
              paint={{
                "fill-color": fireHazardFillColor,
                "fill-opacity": layerOpacities.fire,
                "fill-outline-color": "rgba(0, 0, 0, 0)",
              }}
            />
          </Source>
        )}

        {activeRisk === "flood" && (
          <>
            {showFloodHazardLayer &&
              FLOOD_HAZARD_TILESETS.map((tileset) => (
                <Source
                  key={tileset.id}
                  id={tileset.id}
                  type="vector"
                  url={tileset.url}
                >
                  <Layer
                    {...floodHazardLayer}
                    id={`${tileset.id}-layer`}
                    minzoom={tileset.minzoom}
                    maxzoom={tileset.maxzoom}
                    paint={{
                      ...floodHazardLayer.paint,
                      "fill-color": buildFloodHazardFillColor(
                        tileset.property,
                        tileset.colorStops,
                      ),
                      "fill-opacity": layerOpacities.flood,
                      ...(tileset.useTransparentOutline
                        ? { "fill-outline-color": "rgba(0, 0, 0, 0)" }
                        : {}),
                    }}
                  />
                </Source>
              ))}

            {showFloodRiversLayer && (
              <Source id="flood-rivers" type="vector" url={MAPBOX_TILES.rivers}>
                <Layer
                  {...floodRiversLayer}
                  paint={{
                    ...floodRiversLayer.paint,
                    "line-opacity": layerOpacities.flood,
                  }}
                />
              </Source>
            )}

            {showFloodLakesLayer && (
              <Source id="flood-lakes" type="vector" url={MAPBOX_TILES.lakes}>
                <Layer
                  {...floodLakesLayer}
                  paint={{
                    ...floodLakesLayer.paint,
                    "fill-opacity": layerOpacities.flood,
                  }}
                />
              </Source>
            )}
          </>
        )}

        {showEarthquakeTectonicLayer && (
          <Source
            id="earthquake-tectonic-plates"
            type="vector"
            url={EARTHQUAKE_TECTONIC_SOURCE_URL}
          >
            <Layer
              {...earthquakeTectonicPlatesLayer}
              paint={{
                ...earthquakeTectonicPlatesLayer.paint,
                "line-opacity": layerOpacities.earthquake,
              }}
            />
          </Source>
        )}

        {showEarthquakeFaultsLayer && (
          <Source
            id="earthquake-active-faults"
            type="vector"
            url={EARTHQUAKE_FAULTS_SOURCE_URL}
          >
            <Layer
              {...earthquakeActiveFaultsLayer}
              paint={{
                ...earthquakeActiveFaultsLayer.paint,
                "line-opacity": layerOpacities.earthquake,
              }}
            />
          </Source>
        )}

        {showEarthquakeHazardLayer && (
          <Source
            id="earthquake-risk"
            type="vector"
            url={EARTHQUAKE_HAZARD_SOURCE_URL}
          >
            <Layer
              {...earthquakeHazardFillLayer}
              paint={{
                ...earthquakeHazardFillLayer.paint,
                "fill-opacity": layerOpacities.earthquake,
              }}
            />
          </Source>
        )}

        <MunicipalityScopeLayers
          allowedMunicipalityIds={allowedMunicipalityIds}
          boundarySourceId={MUNICIPALITY_SCOPE_LAYER_IDS.risks.boundarySourceId}
          boundaryFillLayerId={
            MUNICIPALITY_SCOPE_LAYER_IDS.risks.boundaryFillLayerId
          }
          boundaryLineLayerId={
            MUNICIPALITY_SCOPE_LAYER_IDS.risks.boundaryLineLayerId
          }
          maskSourceId={MUNICIPALITY_SCOPE_LAYER_IDS.risks.maskSourceId}
          maskLayerId={MUNICIPALITY_SCOPE_LAYER_IDS.risks.maskLayerId}
          boundaryLineWidth={1.1}
          maskFillOpacity={0.96}
        />
      </Map>

      <MunicipalityMapMenu
        setActiveRisk={setActiveRisk}
        activeRisk={activeRisk}
        fireMapMode={fireMapMode}
        setFireMapMode={setFireMapMode}
        setActiveMapStyle={setActiveMapStyle}
        activeMapStyle={activeMapStyle}
        show3DMap={show3DMap}
        setShow3DMap={setShow3DMap}
        show3DBuildings={show3DBuildings}
        setShow3DBuildings={setShow3DBuildings}
        selectedEarthquakeLayers={selectedEarthquakeLayers}
        setSelectedEarthquakeLayers={setSelectedEarthquakeLayers}
        selectedFloodLayers={selectedFloodLayers}
        setSelectedFloodLayers={setSelectedFloodLayers}
        mapStyle={MAP_STYLE}
        fireHazardColorStops={FIRE_HAZARD_COLOR_STOPS}
        earthquakeHazardColorStops={EARTHQUAKE_HAZARD_COLOR_STOPS}
        floodHazardColorStops={FLOOD_HAZARD_COLOR_STOPS_Z13}
        layerOpacities={layerOpacities}
        setLayerOpacity={setLayerOpacity}
      />
    </div>
  );
}

export default function Container() {
  const { clientInfo } = useClient();
  const mapRef = useRef();

  const { width: drawerWidth, setWidth: setDrawerWidth } = useSidebarWidth({
    storageKey: "artemis.sidebarWidth.municipalityRisksMap",
    minWidth: 440,
  });

  const [menuOpen, setMenuOpen] = React.useState(true);

  const [markerInfo, setMarkerInfo] = useState(null);
  const [activeSideMenuTab, setActiveSideMenuTab] = useState("assessment");
  const [historyAssessmentsForMap, setHistoryAssessmentsForMap] = useState([]);
  const [selectedEarthquakeLayers, setSelectedEarthquakeLayers] = useState([
    "hazard",
  ]);
  const [selectedFloodLayers, setSelectedFloodLayers] = useState(["hazard"]);

  const [formValues, setFormValues] = useState({
    place: null,
    floor: "ΙΣΟΓΕΙΟ",
    latitude: "",
    longitude: "",
    egsa87x: "",
    egsa87y: "",
    name: "",
    notes: "",
    activeAddressType: "address",
    showResults: false,
  });

  const handleMenuToggle = () => {
    setMenuOpen(!menuOpen);
  };

  const municipalityMapScope = useMemo(
    () =>
      getMunicipalityMapScope({
        clientInfo,
        fallbackViewState: FALLBACK_INITIAL_VIEW_STATE,
        mapKeyPrefix: "municipality-risks-map",
      }),
    [clientInfo],
  );

  useEffect(() => {
    setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.getMap().resize();
      }
    }, 300); // matches your transition duration
  }, [menuOpen, drawerWidth]);

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${menuOpen ? drawerWidth : 0}px)` },
          transition: "margin 0.3s",
          marginRight: menuOpen ? `${drawerWidth}px` : 0,
        }}
      >
        <MunicipalityRisksMap
          mapRef={mapRef}
          markerInfo={markerInfo}
          setMarkerInfo={setMarkerInfo}
          setFormValues={setFormValues}
          activeSideMenuTab={activeSideMenuTab}
          setActiveSideMenuTab={setActiveSideMenuTab}
          historyAssessmentsForMap={historyAssessmentsForMap}
          selectedEarthquakeLayers={selectedEarthquakeLayers}
          setSelectedEarthquakeLayers={setSelectedEarthquakeLayers}
          selectedFloodLayers={selectedFloodLayers}
          setSelectedFloodLayers={setSelectedFloodLayers}
          initialViewState={municipalityMapScope.initialViewState}
          mapInstanceKey={municipalityMapScope.mapInstanceKey}
          allowedMunicipalityIds={municipalityMapScope.allowedMunicipalityIds}
        />
      </Box>

      <SideMenu
        open={menuOpen}
        handleToggle={handleMenuToggle}
        mapRef={mapRef}
        setMarkerInfo={setMarkerInfo}
        formValues={formValues}
        setFormValues={setFormValues}
        activeTab={activeSideMenuTab}
        setActiveTab={setActiveSideMenuTab}
        setHistoryAssessmentsForMap={setHistoryAssessmentsForMap}
        selectedEarthquakeLayers={selectedEarthquakeLayers}
        width={drawerWidth}
        onResize={setDrawerWidth}
      />
    </Box>
  );
}
